import { cx } from 'class-variance-authority';
import { FC, HTMLAttributes, useEffect, useRef, useState } from 'react';

export type RangeProps = HTMLAttributes<HTMLInputElement> & {
  name: string;
  range: {
    min: number;
    max: number;
  };
  getThumbText?: (value: number) => string;
  step?: number;
  initialValue: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const RangeComponent: FC<RangeProps> = ({ className, ...props }) => {
  const getSelectedRange = (value: number) => {
    const min = props.range.min;
    const max = props.range.max;

    return ((value - min) * 100) / (max - min);
  };

  const [value, setValue] = useState(props.initialValue);
  const [selectedRange, setSelectedRange] = useState<number>(getSelectedRange(props.initialValue));

  const _onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRange(getSelectedRange(Number(e.currentTarget.value)));
    setValue(Number(e.currentTarget.value));
    props.onChange(e);
  };

  const unitRef = useRef<HTMLDivElement | null>(null);
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [left, setLeft] = useState('0px');

  useEffect(() => {
    // istanbul ignore if
    if (!unitRef.current || !parentRef.current) return;

    const updateLeftPosition = () => {
      const parentRect = parentRef.current!.getBoundingClientRect();
      const bubbleRect = unitRef.current!.getBoundingClientRect();

      const rawLeft = (selectedRange / 100) * parentRect.width + (8 - selectedRange * 0.15);
      const minLeft = 0;
      const maxLeft = parentRect.width - bubbleRect.width;
      const clampedLeft = Math.min(Math.max(rawLeft, minLeft), maxLeft);
      setLeft(`${clampedLeft}px`);
    };

    updateLeftPosition();

    const resizeObserver = new ResizeObserver(() => {
      updateLeftPosition();
    });

    resizeObserver.observe(unitRef.current);

    // Also update left position on window resize, to recalc the parent's width
    window.addEventListener('resize', updateLeftPosition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateLeftPosition);
    };
  }, [selectedRange, value]);

  return (
    <div
      ref={parentRef}
      className="relative box-border h-3 w-full min-w-48 rounded-md border border-black-40 bg-black-20"
    >
      <div
        className={`absolute bottom-0 left-0 right-0 top-0 h-3 min-w-2 rounded-lg bg-success-500`}
        style={{
          width: `calc(${selectedRange}% + (${8 - selectedRange * 0.15}px))`,
        }}
      />
      <div
        ref={unitRef}
        className={`absolute top-full -translate-x-[4px] pt-2 text-body-small font-bold text-white`}
        style={{
          left,
        }}
      >
        {props.getThumbText ? props.getThumbText(value) : value}
      </div>
      <input
        name={props.name}
        aria-label={props.name}
        value={value}
        type="range"
        min={props.range.min}
        max={props.range.max}
        step={props.step ?? 1}
        className={cx(
          'hm-range absolute -left-[1px] -right-[1px] top-0 h-3 appearance-none rounded-md bg-transparent',
          className
        )}
        onChange={_onChange}
      />
    </div>
  );
};

export const Range = RangeComponent;
